import { ensureError } from '@hover/utilities';
import { useCallback } from 'react';

import { request } from 'src/shared/utils/request';

import { logWarning } from '../telemetry/logger';
import { getUtmCookies } from '../utils/cookie';

import { useLocation } from './useLocation';

export interface SendHubSpotCustomEventOptions {
  eventName: string;
  email: string;
}

export interface HubSpotCustomEventData {
  utmSrc: string;
  hs_page_url: string;
  utmCampaign: string | null;
  campaignID: string | null;
  utmMedium: string | null;
}

type Properties = Pick<SendHubSpotCustomEventOptions, 'email'> &
  HubSpotCustomEventData;

// TODO: move this endpoint from legacy CloudFlare Pages static marketing site
// deployment to an Express endpoint in this application's backend
const endpoint =
  'https://7346f1a9.hover-static.pages.dev/pages-api/hubspot/event-submit';

export const useSendCustomHubSpotEvent = () => {
  const { search, href } = useLocation();

  return useCallback(
    async ({
      email,
      eventName,
      ...overrides
    }: SendHubSpotCustomEventOptions & Partial<HubSpotCustomEventData>) => {
      if (email.includes('@example.com')) return;

      const queryParams = new URLSearchParams(search);

      const urlData = {
        utmCampaign: queryParams.get('utm_campaign'),
        utmSrc: queryParams.get('utmSrc') || 'direct',
        utmMedium: queryParams.get('utm_medium'),
        campaignID: queryParams.get('campaign_id'),
        hs_page_url: href,
      };

      const cookieData = getUtmCookies();

      const { utmSrc, utmCampaign, campaignID, utmMedium }: Properties = {
        ...cookieData,
        ...urlData,
        ...overrides,
        email,
      };

      const body = JSON.stringify({
        eventName,
        properties: {
          email,
          traffic_src: utmSrc,
          query_params: search,
          hs_utm_source: utmSrc,
          hs_page_url: href,
          hs_utm_campaign: utmCampaign ?? '',
          hs_campaign_id: campaignID ?? '',
          hs_utm_medium: utmMedium ?? '',
        },
        objectType: 'contacts',
        email,
      });

      const requestOptions = {
        method: 'POST',
        body,
      };

      try {
        await request(endpoint, requestOptions);
      } catch (caught) {
        const error = ensureError(caught);

        logWarning(
          `Failed to track HubSpot event "${eventName}"\n${error.message}\n${
            error.stack ?? '[no stack]'
          }`,
        );
      }
    },
    [href, search],
  );
};
